/**
 * AccountSummary component - Financial summary box for invoices
 *
 * Renders the account summary with totals, subtotals, and amount due.
 * Uses green background for totals and yellow for amount due.
 */

import type { Content, ContentTable, TableCell, TableLayout } from 'pdfmake/interfaces';
import type { AccountSummaryData, AccountSummaryRow } from '../types';
import { PDF_COLORS } from '../types';

/**
 * Format currency value
 */
function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

/**
 * Create an account summary section for PDF
 *
 * @param data - Account summary data with rows
 * @returns pdfmake Content for the account summary
 */
export function createAccountSummary(data: AccountSummaryData): Content {
    const {
        rows,
        totalBackground = PDF_COLORS.totalGreen,
        amountDueBackground = PDF_COLORS.amountDueYellow,
    } = data;

    const body: TableCell[][] = [];

    for (const row of rows) {
        // Default to green background for the whole box
        let fillColor: string = totalBackground;
        let bold = false;
        let leftPadding = 6;
        let fontSize = 9;

        switch (row.type) {
            case 'total':
                bold = true;
                break;
            case 'header':
                // Regular weight (e.g., "Withdrawals", "Payments")
                bold = false;
                break;
            case 'amount-due':
                fillColor = amountDueBackground;
                bold = true;
                break;
            case 'subtotal':
                leftPadding = 15; // Indent subtotals
                fontSize = 8;
                break;
        }

        // Build label - if there's a sub-label (date), put it at the start
        let labelText = row.label;
        if (row.subLabel) {
            labelText = `${row.subLabel} ${row.label}`;
        }

        const labelCell: TableCell = {
            text: labelText,
            fillColor,
            bold,
            fontSize,
            margin: [leftPadding, 2, 0, 2],
        };

        const valueCell: TableCell = {
            text: formatCurrency(row.amount),
            fillColor,
            bold,
            fontSize,
            alignment: 'right',
            margin: [0, 2, 6, 2],
        };

        body.push([labelCell, valueCell]);
    }

    // Green border color to match the green theme
    const boxBorderColor = '#4caf50';

    const tableLayout: TableLayout = {
        // Only outer borders - no inner horizontal lines
        hLineWidth: (i: number) => (i === 0 || i === body.length ? 1 : 0),
        vLineWidth: (i: number, node: ContentTable) => (i === 0 || i === node.table.widths.length ? 1 : 0),
        hLineColor: () => boxBorderColor,
        vLineColor: () => boxBorderColor,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
    };

    const content: ContentTable = {
        table: {
            widths: ['*', 90],
            body,
        },
        layout: tableLayout,
    };

    return content;
}

/**
 * Build account summary rows from invoice info
 * This is a helper to convert InvoiceInfo to AccountSummaryData
 */
export function buildAccountSummaryRows(info: {
    taskTotal: number;
    expenseTotal: number;
    flatFeeTotal: number;
    adjustments: Array<{ notes?: string; amount: number }>;
    adjustmentTotal: number;
    withdrawals: Array<{ notes?: string; amount: number }>;
    withdrawalsTotal: number;
    refunds: Array<{ notes?: string; amount: number }>;
    refundTotal: number;
    discounts: Array<{ reason?: string; reasonLabel?: string; amount: number }>;
    discountTotal: number;
    currentInvoiceAmount: number;
    pastDue: number;
    currentBalance: number;
    payments: Array<{ txDate: Date | string; description?: string; txType?: string; amount: number }>;
    paymentTotal: number;
    amountDue: number;
    isLead?: boolean;
    taxTotal?: number;
    interestTotal?: number;
    taxLabel?: string;
}): AccountSummaryRow[] {
    const rows: AccountSummaryRow[] = [];

    // Previous Balance at top (not for leads)
    if (!info.isLead) {
        rows.push({
            label: 'Previous Balance',
            amount: info.pastDue,
            type: 'total',
        });
    }

    // Labor and Expenses total
    rows.push({
        label: 'Labor and Expenses',
        amount: info.taskTotal + info.expenseTotal + info.flatFeeTotal,
        type: 'total',
    });

    // Labor subtotal
    rows.push({
        label: 'Labor',
        amount: info.taskTotal,
        type: 'subtotal',
    });

    // Flat Fees (if any)
    if (info.flatFeeTotal > 0) {
        rows.push({
            label: 'Flat Fees',
            amount: info.flatFeeTotal,
            type: 'subtotal',
        });
    }

    // Expenses subtotal
    rows.push({
        label: 'Expenses',
        amount: info.expenseTotal,
        type: 'subtotal',
    });

    // Adjustments (no background on header)
    if (info.adjustmentTotal > 0) {
        rows.push({
            label: 'Adjustments',
            amount: -info.adjustmentTotal,
            type: 'header',
        });

        for (const adj of info.adjustments) {
            rows.push({
                label: adj.notes || 'Adjustment',
                amount: -adj.amount,
                type: 'subtotal',
            });
        }
    }

    // Withdrawals (no background on header)
    if (info.withdrawalsTotal > 0) {
        rows.push({
            label: 'Withdrawals',
            amount: -info.withdrawalsTotal,
            type: 'header',
        });

        for (const wd of info.withdrawals) {
            rows.push({
                label: wd.notes || 'Withdrawal',
                amount: -wd.amount,
                type: 'subtotal',
            });
        }
    }

    // Refunds (no background on header)
    if (info.refundTotal > 0) {
        rows.push({
            label: 'Refunds',
            amount: info.refundTotal,
            type: 'header',
        });

        for (const ref of info.refunds) {
            rows.push({
                label: ref.notes || 'Refund',
                amount: ref.amount,
                type: 'subtotal',
            });
        }
    }

    // Discounts (no background on header)
    if (info.discountTotal > 0) {
        rows.push({
            label: 'Discounts',
            amount: -info.discountTotal,
            type: 'header',
        });

        for (const disc of info.discounts) {
            rows.push({
                label: disc.reasonLabel || disc.reason || 'Discount',
                amount: -disc.amount,
                type: 'subtotal',
            });
        }
    }

    // Additions this Period
    rows.push({
        label: 'Additions this Period',
        amount: info.currentInvoiceAmount,
        type: 'total',
    });

    // Tax (if any)
    if (info.taxTotal && info.taxTotal > 0) {
        rows.push({
            label: info.taxLabel || 'Tax',
            amount: info.taxTotal,
            type: 'total',
        });
    }

    // Interest (if any)
    if (info.interestTotal && info.interestTotal > 0) {
        rows.push({
            label: 'Interest',
            amount: info.interestTotal,
            type: 'total',
        });
    }

    // Balance Before Payments
    rows.push({
        label: 'Balance Before Payments',
        amount: info.currentBalance + (info.taxTotal || 0) + (info.interestTotal || 0),
        type: 'total',
    });

    // Payments (no background on header)
    rows.push({
        label: 'Payments',
        amount: -info.paymentTotal,
        type: 'header',
    });

    for (const pmt of info.payments) {
        // Format date as MM/DD/YY to match original
        const date = pmt.txDate instanceof Date ? pmt.txDate : new Date(pmt.txDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        const dateStr = `${month}/${day}/${year}`;

        const isTransferToTrust = pmt.txType === 'TT';
        rows.push({
            label: pmt.description || 'Payment',
            subLabel: dateStr,
            amount: isTransferToTrust ? pmt.amount : -pmt.amount,
            type: 'subtotal',
        });
    }

    // Amount Due
    rows.push({
        label: 'Amount Due',
        amount: info.amountDue,
        type: 'amount-due',
    });

    return rows;
}

export default createAccountSummary;
